import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    HStack,
    IconProps,
    Text
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import {
    ActivityIcon,
    BookOpenIcon,
    BriefcaseIcon,
    CalendarIcon,
    CheckSquareIcon,
    ClipboardIcon,
    FileTextIcon,
    GridIcon,
    MailIcon,
    MessageCircleIcon,
    SettingsIcon,
    TargetIcon,
    UserIcon,
    UsersIcon,
} from '@/imports/ui/chakra/feather';

import { MenteeIcon, MentorIcon } from './ParticipantIcons';

export type MentorshipModuleKey =
  | 'mentorship'
  | 'admin'
  | 'mentor'
  | 'mentee'
  | 'dashboard'
  | 'workspace'
  | 'programs'
  | 'discussions'
  | 'mentees'
  | 'peers'
  | 'goals'
  | 'private-goals'
  | 'tasks'
  | 'resources'
  | 'sessions'
  | 'incoming-requests'
  | 'activity-timeline'
  | 'matching'
  | 'participants'
  | 'profile'
  | 'profile-applications'
  | 'program-applications'
  | 'application-forms'
  | 'templates'
  | 'email-templates'
  | 'notifications'
  | 'chat'
  | 'audit-log'
  | 'settings';

type ModulePresentation = {
  label: string;
  icon: React.ComponentType<IconProps>;
};

export { MenteeIcon, MentorIcon } from './ParticipantIcons';

export const MENTORSHIP_MODULE_PRESENTATION: Record<
  MentorshipModuleKey,
  ModulePresentation
> = {
  mentorship: { label: 'Mentorship', icon: UsersIcon },
  admin: { label: 'Admin', icon: SettingsIcon },
  mentor: { label: 'Mentor', icon: MentorIcon },
  mentee: { label: 'Mentee', icon: MenteeIcon },
  dashboard: { label: 'Dashboard', icon: GridIcon },
  workspace: { label: 'Workspace', icon: BriefcaseIcon },
  programs: { label: 'Programs', icon: BookOpenIcon },
  discussions: { label: 'Discussions', icon: MessageCircleIcon },
  mentees: { label: 'Mentees', icon: MenteeIcon },
  peers: { label: 'Peers', icon: UsersIcon },
  goals: { label: 'Goals', icon: TargetIcon },
  'private-goals': { label: 'Private Goals', icon: TargetIcon },
  tasks: { label: 'Tasks', icon: CheckSquareIcon },
  resources: { label: 'Resources', icon: FileTextIcon },
  sessions: { label: 'Sessions', icon: CalendarIcon },
  'incoming-requests': { label: 'Incoming Requests', icon: CalendarIcon },
  'activity-timeline': { label: 'Activity Timeline', icon: ActivityIcon },
  matching: { label: 'Matching', icon: UsersIcon },
  participants: { label: 'Participants', icon: UsersIcon },
  profile: { label: 'Profile', icon: UserIcon },
  'profile-applications': {
    label: 'Profile Applications',
    icon: ClipboardIcon,
  },
  'program-applications': {
    label: 'Program Applications',
    icon: ClipboardIcon,
  },
  'application-forms': { label: 'Application Forms', icon: FileTextIcon },
  templates: { label: 'Templates', icon: FileTextIcon },
  'email-templates': { label: 'Email Templates', icon: MailIcon },
  notifications: { label: 'Notifications', icon: MailIcon },
  chat: { label: 'Chat', icon: MessageCircleIcon },
  'audit-log': { label: 'Audit Log', icon: ActivityIcon },
  settings: { label: 'Settings', icon: SettingsIcon },
};

export const MentorshipModuleIcon: React.FC<
  { module: MentorshipModuleKey } & IconProps
> = ({ module, ...props }) => {
  const Icon = MENTORSHIP_MODULE_PRESENTATION[module].icon;
  return <Icon aria-hidden="true" {...props} />;
};

export const mentorshipModuleLabel = (module: MentorshipModuleKey) =>
  MENTORSHIP_MODULE_PRESENTATION[module].label;

export const mentorshipModuleFromHref = (href: string): MentorshipModuleKey => {
  if (href.includes('/profile-applications')) return 'profile-applications';
  if (href.includes('/program-applications')) return 'program-applications';
  if (href.includes('/email-templates')) return 'email-templates';
  if (href.includes('/notifications')) return 'notifications';
  if (href.includes('/chat')) return 'chat';
  if (href.includes('/application')) return 'application-forms';
  if (href.includes('/templates')) return 'templates';
  if (href.includes('/audit-log')) return 'audit-log';
  if (href.includes('/dashboard')) return 'dashboard';
  if (href.includes('/workspaces') || href.endsWith('/workspace')) {
    return 'workspace';
  }
  if (href.includes('/programs')) return 'programs';
  if (href.includes('/matching')) return 'matching';
  if (href.includes('/mentors')) return 'mentor';
  if (href.includes('/mentees')) return 'mentees';
  if (href.includes('/resources')) return 'resources';
  if (href.includes('/settings')) return 'settings';
  return 'mentorship';
};

export const mentorshipModuleFromTitle = (
  title: string
): MentorshipModuleKey => {
  const normalized = title.toLowerCase();
  const match = (
    Object.keys(MENTORSHIP_MODULE_PRESENTATION) as MentorshipModuleKey[]
  )
    .filter((key) => key !== 'mentorship')
    .find((key) =>
      normalized.includes(
        MENTORSHIP_MODULE_PRESENTATION[key].label.toLowerCase()
      )
    );
  return match ?? 'mentorship';
};

export type MentorshipBreadcrumbItem = {
  module: MentorshipModuleKey;
  label?: string;
  href?: string;
};

export const MentorshipBreadcrumbs: React.FC<{
  items: MentorshipBreadcrumbItem[];
}> = ({ items }) => (
  <Breadcrumb separator=">" fontSize="sm" color="gray.500">
    {items.map((item, index) => {
      const isCurrent = index === items.length - 1;
      const content = (
        <HStack spacing="1.5">
          <MentorshipModuleIcon
            module={item.module}
            boxSize="14px"
            color={isCurrent ? 'primary.500' : 'gray.500'}
          />
          <Text
            fontWeight={isCurrent ? '700' : '500'}
            color={isCurrent ? 'primary.600' : 'gray.600'}
          >
            {item.label ?? mentorshipModuleLabel(item.module)}
          </Text>
        </HStack>
      );
      return (
        <BreadcrumbItem
          key={`${item.module}:${item.label ?? index}`}
          isCurrentPage={isCurrent}
        >
          {item.href && !isCurrent ? (
            <BreadcrumbLink as={NextLink} href={item.href}>
              {content}
            </BreadcrumbLink>
          ) : (
            <BreadcrumbLink aria-current={isCurrent ? 'page' : undefined}>
              {content}
            </BreadcrumbLink>
          )}
        </BreadcrumbItem>
      );
    })}
  </Breadcrumb>
);
